import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useApiCall } from '@/hooks';
import parse from 'html-react-parser';
import {
  UnsubscribeByTokenRequest,
  UnsubscribeByTokenResponse,
  UnsubscribeTokenVerifyRequest,
  UnsubscribeTokenVerifyResponse,
  amlbotAPI,
} from '@/api';
import { AlertBox, AuthContainer, Button } from '@/components';
import { toast } from 'react-toastify';
import { COMMON_CONTAINER_ID } from '@/config';
import styles from './ReminderUnsubscribe.module.sass';

interface ReminderUnsubscribeProps {}

const ReminderUnsubscribe: FC<ReminderUnsubscribeProps> = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(searchParams.get('token'));
  const [userId, setUserId] = useState<number | null>(null);
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);

  const [unsubscribe, loadingUnsubscribe, errorUnsubscribe] = useApiCall(
    async () => {
      if (!token || !userId) {
        return;
      }

      const payload: UnsubscribeByTokenRequest = {
        user_id: userId,
        token,
      };

      const response: UnsubscribeByTokenResponse =
        await amlbotAPI.unsubscribeByToken(payload);

      if (response.data.success) {
        setIsUnsubscribed(() => true);
        toast.success(t('translation.receive_reminders_updated'), {
          containerId: COMMON_CONTAINER_ID,
        });
      }
    },
  );

  const [verifyToken, loading, error] = useApiCall(async () => {
    if (!token) {
      return;
    }

    const payload: UnsubscribeTokenVerifyRequest = {
      token: token || '',
    };
    const response: UnsubscribeTokenVerifyResponse =
      await amlbotAPI.verifyUnsubscribeToken(payload);

    if (!!response.data.user_id) {
      setUserId(() => response.data.user_id);
    }
  });

  useEffect(() => {
    verifyToken();
  }, []);

  const submitHandler = () => {
    unsubscribe();
  };

  const description = useMemo(() => {
    if (error) {
      return '';
    }

    return isUnsubscribed
      ? t('translation.reminder_unsubscribe_success')
      : t('translation.reminder_token_description');
  }, [error]);

  const title = t('translation.reminder_section_title');

  return (
    <AuthContainer
      title={title}
      description={description}
      alert={
        !!error && (
          <AlertBox type="danger">
            {{
              title: parse(t('translation.error')),
              description: error,
              icon: '🤔',
            }}
          </AlertBox>
        )
      }
      buttons={
        <div>
          {!isUnsubscribed && !error && (
            <Button
              size="xl"
              corner="rect"
              onClick={submitHandler}
              disabled={loading || loadingUnsubscribe}
              isLoading={loading || loadingUnsubscribe}
              fullwidth
            >
              {t('translation.reminders_off_btn')}
            </Button>
          )}
        </div>
      }
    />
  );
};

export default ReminderUnsubscribe;
